import { Link } from "react-router-dom";
import { usePoll } from "@/hooks/usePoll";
import { useAdmin, ROLE_LABEL } from "@/admin/AdminContext";
import { PageHeader } from "@/components/Badges";
import { timeAgo } from "@/lib/api";

const Card = ({ label, value, sub, c = "text-zinc-900", testid, to }) => {
  const body = (
    <div className="panel p-4 h-full hover:border-zinc-300 transition-colors" data-testid={testid}>
      <p className="label-xs mb-2">{label}</p><p className={`num text-2xl font-semibold ${c}`}>{value ?? "—"}</p>
      {sub && <p className="text-[11px] text-zinc-500 mt-1">{sub}</p>}
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
};

export const actionLabel = (a) => a.replace(/\./g, " · ").replace(/_/g, " ");

export default function Overview() {
  const { staff } = useAdmin();
  const { data: d } = usePoll("/admin/overview", 30000);
  if (!d) return <p className="label-xs">Loading overview…</p>;
  return (
    <div data-testid="admin-overview-page">
      <PageHeader title={`Welcome, ${staff.name?.split(" ")[0] || ROLE_LABEL[staff.role]}`} sub={`${ROLE_LABEL[staff.role]} · scope: ${d.scope}`} />
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3 mb-6">
        <Card label="Total accounts" value={d.total_users} sub={`${d.new_7d} new in 7 days`} testid="ov-total" to="/admin/users" />
        <Card label="Pending approval" value={d.status.pending} c="text-[#D97706]" testid="ov-pending" to="/admin/users" />
        <Card label="Active" value={d.status.approved} c="text-[#059669]" testid="ov-active" />
        <Card label="Suspended" value={d.status.suspended} c="text-[#DC2626]" testid="ov-suspended" />
        <Card label="Subscribed" value={d.subscribed} sub={`${d.active_plans}/${d.plans} plans active`} c="text-[#7C3AED]" testid="ov-subscribed" />
        <Card label="Open paper trades" value={d.open_paper_trades} sub={`${d.signals_tracked} signals tracked`} testid="ov-trades" />
      </div>
      <div className="grid grid-cols-12 gap-4">
        <section className="col-span-12 lg:col-span-4 panel p-4" data-testid="ov-roles">
          <p className="label-xs mb-3">Accounts by role</p>
          {Object.entries(d.roles).map(([r, n]) => (
            <div key={r} className="flex items-center gap-3 py-2 border-b border-zinc-100 text-sm">
              <span className="w-36 text-zinc-600">{ROLE_LABEL[r]}</span>
              <div className="flex-1 h-1.5 bg-zinc-100"><div className="h-full bg-[#7C3AED]" style={{ width: `${d.total_users ? (n / d.total_users) * 100 : 0}%` }} /></div>
              <span className="num w-8 text-right">{n}</span>
            </div>
          ))}
        </section>
        <section className="col-span-12 lg:col-span-8 panel" data-testid="ov-recent-logs">
          <div className="flex justify-between items-center px-4 py-3 border-b border-zinc-200">
            <h3 className="font-display font-bold text-sm">Recent admin activity</h3>
            <Link to="/admin/logs" className="text-xs text-[#7C3AED] hover:underline">All logs →</Link>
          </div>
          {d.recent_logs.map((l, i) => (
            <div key={i} className="px-4 py-2.5 border-b border-zinc-100 flex items-center gap-3 text-sm">
              <span className="num text-[11px] text-zinc-400 w-16">{timeAgo(l.created_at)}</span>
              <span className="text-zinc-900 font-medium">{l.actor_email}</span>
              <span className="text-zinc-500 capitalize">{actionLabel(l.action)}</span>
              {l.target_label && <span className="text-zinc-700 truncate">{l.target_label}</span>}
            </div>
          ))}
          {!d.recent_logs.length && <p className="p-4 text-sm text-zinc-500">No activity yet.</p>}
        </section>
      </div>
    </div>
  );
}
